import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { timeAgo } from "@/lib/timeAgo";
import { eventIcon, eventIconColor } from "@/lib/notificationTypes";
import { CheckCircle, ArrowCounterClockwise, BellSlash } from "@phosphor-icons/react";
import { toast } from "sonner";

const TABS = [
    { key: "all", label: "All" },
    { key: "unread", label: "Unread" },
    { key: "read", label: "Read" },
];
const PAGE_SIZE = 20;

function ListSkeleton() {
    return (
        <div className="bg-white border border-brand-line rounded-sm divide-y divide-brand-line/60">
            {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="p-4 flex gap-3 animate-pulse">
                    <div className="w-8 h-8 rounded-full bg-brand-bg shrink-0" />
                    <div className="flex-1 space-y-2">
                        <div className="h-3.5 bg-brand-bg rounded w-1/3" />
                        <div className="h-3 bg-brand-bg rounded w-2/3" />
                    </div>
                </div>
            ))}
        </div>
    );
}

// Notification Center V2 — full inbox at /app/notifications. Consumes the SAME
// notifications collection/API as the header bell (NotificationBell.jsx); no separate model.
export default function Notifications() {
    const nav = useNavigate();
    const [tab, setTab] = useState("all");
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [markingAll, setMarkingAll] = useState(false);

    const loadUnreadCount = useCallback(async () => {
        try {
            const { data } = await api.get("/notifications/unread-count");
            setUnreadCount(data?.unread_count || 0);
        } catch { /* non-critical for the header stat */ }
    }, []);

    const load = useCallback(async (targetPage, status) => {
        setError(false);
        try {
            const { data } = await api.get("/notifications", { params: { page: targetPage, limit: PAGE_SIZE, status: status === "all" ? undefined : status } });
            setItems((prev) => (targetPage === 1 ? data.items : [...prev, ...data.items]));
            setTotal(data.total);
            setHasMore(data.has_more);
            setPage(data.page);
        } catch (e) {
            setError(true);
            toast.error(formatError(e));
        }
    }, []);

    useEffect(() => {
        setLoading(true);
        Promise.all([load(1, tab), loadUnreadCount()]).finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    async function handleLoadMore() {
        setLoadingMore(true);
        await load(page + 1, tab);
        setLoadingMore(false);
    }

    async function handleMarkAllRead() {
        setMarkingAll(true);
        try {
            await api.post("/notifications/read-all");
            setItems((rows) => rows.map((r) => ({ ...r, read: true })));
            setUnreadCount(0);
            if (tab === "unread") { setItems([]); setTotal(0); setHasMore(false); }
            toast.success("All notifications marked as read");
        } catch (e) { toast.error(formatError(e)); } finally { setMarkingAll(false); }
    }

    async function toggleRead(n, e) {
        e.stopPropagation();
        try {
            await api.post(`/notifications/${n.id}/${n.read ? "unread" : "read"}`);
            setUnreadCount((c) => (n.read ? c + 1 : Math.max(0, c - 1)));
            if (tab === "all") {
                setItems((rows) => rows.map((r) => (r.id === n.id ? { ...r, read: !r.read } : r)));
            } else {
                setItems((rows) => rows.filter((r) => r.id !== n.id));
                setTotal((t) => Math.max(0, t - 1));
            }
        } catch (e2) { toast.error(formatError(e2)); }
    }

    async function handleOpen(n) {
        if (!n.read) {
            try {
                await api.post(`/notifications/${n.id}/read`);
                setUnreadCount((c) => Math.max(0, c - 1));
                setItems((rows) => rows.map((r) => (r.id === n.id ? { ...r, read: true } : r)));
            } catch { /* still navigate even if the read-mark call fails */ }
        }
        if (n.deep_link) nav(n.deep_link);
    }

    return (
        <div data-testid="notifications-page" className="animate-fade-up">
            <PageHeader
                overline="Inbox"
                title="Notifications"
                description={unreadCount > 0 ? `${unreadCount} unread notification${unreadCount === 1 ? "" : "s"}` : "You're all caught up."}
                actions={unreadCount > 0 && (
                    <Button data-testid="mark-all-read-btn" variant="outline" onClick={handleMarkAllRead} disabled={markingAll} className="rounded-full border-brand-line">
                        <CheckCircle size={15} className="mr-1.5" /> {markingAll ? "Marking…" : "Mark all as read"}
                    </Button>
                )}
            />

            <div className="flex items-center gap-2 mb-5" data-testid="notification-tabs">
                {TABS.map((t) => (
                    <button key={t.key} data-testid={`notification-tab-${t.key}`} onClick={() => setTab(t.key)}
                        className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-overline font-medium transition-colors ${
                            tab === t.key ? "bg-brand-ink text-white" : "bg-white border border-brand-line text-brand-inkSoft hover:bg-brand-bg"
                        }`}>
                        {t.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <ListSkeleton />
            ) : error ? (
                <EmptyState title="Unable to load notifications" description="Please try again." action={
                    <Button data-testid="notifications-retry-btn" variant="outline" onClick={() => load(1, tab)} className="rounded-full">Retry</Button>
                } />
            ) : items.length === 0 ? (
                <EmptyState
                    title={tab === "unread" ? "You're all caught up." : tab === "read" ? "No read notifications" : "No notifications yet"}
                    description={tab === "all" ? "You'll see updates here as they happen — like when a vehicle you registered gets approved or rejected." : undefined}
                />
            ) : (
                <>
                    <div className="bg-white border border-brand-line rounded-sm divide-y divide-brand-line/60" data-testid="notification-list">
                        {items.map((n) => {
                            const Icon = eventIcon(n.event_type);
                            return (
                                <div key={n.id} data-testid={`notification-row-${n.id}`} role="button" tabIndex={0}
                                    onClick={() => handleOpen(n)}
                                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleOpen(n); } }}
                                    className={`w-full text-left p-4 flex gap-3 items-start hover:bg-brand-bg/50 transition-colors cursor-pointer ${!n.read ? "bg-brand-sage/15" : ""}`}>
                                    <span className={`w-8 h-8 rounded-full bg-brand-bg flex items-center justify-center shrink-0 ${eventIconColor(n.event_type)}`}>
                                        <Icon size={17} weight="duotone" />
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <p className="text-sm font-medium text-brand-ink truncate">{n.title}</p>
                                            {!n.read && <span data-testid={`notification-unread-dot-${n.id}`} className="w-1.5 h-1.5 rounded-full bg-brand-action shrink-0" />}
                                        </div>
                                        <p className="text-sm text-brand-inkSoft mt-0.5 break-words">{n.message}</p>
                                        <p className="text-[11px] text-brand-inkSoft/70 mt-1.5">{timeAgo(n.created_at)}</p>
                                    </div>
                                    <button data-testid={`notification-toggle-read-${n.id}`} onClick={(e) => toggleRead(n, e)}
                                        title={n.read ? "Mark as unread" : "Mark as read"}
                                        className="p-1.5 rounded-full hover:bg-brand-bg text-brand-inkSoft shrink-0">
                                        {n.read ? <ArrowCounterClockwise size={15} /> : <CheckCircle size={15} />}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                    <div className="text-center mt-5">
                        {hasMore ? (
                            <Button data-testid="notifications-load-more-btn" variant="outline" onClick={handleLoadMore} disabled={loadingMore} className="rounded-full border-brand-line">
                                {loadingMore ? "Loading…" : "Load more"}
                            </Button>
                        ) : (
                            <p data-testid="notifications-end-of-list" className="text-xs text-brand-inkSoft flex items-center justify-center gap-1.5">
                                <BellSlash size={13} /> No more notifications
                            </p>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
